import { type ApolloError } from 'apollo-client'
import {
  PartnerPickDeviceAccountTypeEnum,
  type PartnerPickDevicesFormData,
} from 'domains/partner-pick-devices/types/partnerPickDevices.types'
import { DeviceTypeEnum, usePartnerPickDeviceQuery } from 'domains/partner-pick-devices/api'
import { mapPartnerPickDeviceQueryResultToFormData } from 'domains/partner-pick-devices/components/form/utils/PartnerPickDevicesForm.utils'

interface UsePartnerPickDevicesFormDataProps {
  deviceId?: string
}

interface UsePartnerPickDevicesFormDataResult {
  formData?: PartnerPickDevicesFormData
  formDataLoading: boolean
  formDataError?: ApolloError
  refetch?: () => void
}

const initialFormData: PartnerPickDevicesFormData = {
  deviceType: DeviceTypeEnum.Phone,
  accountType: PartnerPickDeviceAccountTypeEnum.Shopping,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
}

/**
 * Returns the initial values for the form, as well as details regarding the loading state
 * for those values.
 *
 * If we are in "Update" mode, then we need to fetch the existing device
 * data to pre-fill the form. In "Create" mode, we just skip this and return the default
 * values.
 */
export const usePartnerPickDevicesFormData = ({
  deviceId,
}: UsePartnerPickDevicesFormDataProps): UsePartnerPickDevicesFormDataResult => {
  const { data, loading, error, refetch } = usePartnerPickDeviceQuery({
    variables: {
      id: deviceId,
    },
    skip: !deviceId,
  })

  if (deviceId) {
    return {
      formData: data
        ? mapPartnerPickDeviceQueryResultToFormData(data.partnerPickDevice)
        : undefined,
      formDataError: error,
      formDataLoading: loading,
      refetch,
    }
  }

  return {
    formData: initialFormData,
    formDataLoading: false,
  }
}
